import React, { useState, useEffect, useRef } from 'react';
import { EELogo } from './EEBrand';
import { useAuth } from '../auth/AuthContext';
import UserDropdown from './UserDropdown';
import { useDashboardTheme } from '../theme/DashboardTheme';

// ── Íconos ───────────────────────────────────────────────────────
// Sistema de notificaciones in-app eliminado (Telegram es el canal
// oficial de alertas + tabla del dashboard + historial cubren la
// misma información). Solo dejamos el icono que sigue en uso.
const I = ({ children, size = 14 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none"
    stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    {children}
  </svg>
);
const IconRefresh = () => <I><path d="M20 11a8 8 0 1 0-2.3 5.7M20 4v5h-5"/></I>;
const IconSun = () => <I size={16}><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"/></I>;
const IconMoon = () => <I size={16}><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></I>;

// ── Topbar principal ─────────────────────────────────────────────
export default function Topbar({
  connected, lastUpdate, getAgeStr, getAgeColor, onMenuOpen, picks = [],
}) {
  const [clock, setClock]     = useState('');
  const [nowTick, setNowTick] = useState(Date.now());

  // Avatar trigger mobile — abre UserDropdown (que en mobile renderiza
  // como bottom sheet vía CSS @media en dropdown-modal.css).
  const { user, userData } = useAuth();
  const { theme, toggleTheme } = useDashboardTheme();
  const avatarRef = useRef(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [triggerRect, setTriggerRect] = useState(null);

  const displayName =
    userData?.nombre ||
    user?.displayName ||
    (user?.email ? user.email.split('@')[0] : 'Usuario');
  const initials = displayName.split(' ')[0].slice(0, 2).toUpperCase();

  const toggleDropdown = () => {
    if (avatarRef.current) {
      setTriggerRect(avatarRef.current.getBoundingClientRect());
    }
    setDropdownOpen((v) => !v);
  };

  // Calcular edad del ultimo tick del motor.
  // Mira el timestamp más reciente entre todos los picks activos
  // (lastUpdate puede quedarse pegado si el motor está offline).
  const lastTickMs = (() => {
    if (picks && picks.length > 0) {
      let maxTs = 0;
      for (const p of picks) {
        if (!p.timestamp) continue;
        let ts = 0;
        if (typeof p.timestamp === 'number') {
          ts = p.timestamp;
        } else if (typeof p.timestamp === 'string') {
          const parsed = new Date(p.timestamp).getTime();
          if (!isNaN(parsed)) ts = parsed;
        }
        if (ts > maxTs) maxTs = ts;
      }
      if (maxTs > 0) return maxTs;
    }
    if (!lastUpdate) return 0;
    if (lastUpdate instanceof Date) return lastUpdate.getTime();
    if (typeof lastUpdate === 'number') return lastUpdate;
    const v = parseInt(lastUpdate, 10);
    return isNaN(v) ? 0 : v;
  })();
  const ageSeconds = lastTickMs > 0 ? Math.floor((nowTick - lastTickMs) / 1000) : 9999;
  const motorOffline = ageSeconds > 120; // 2 minutos sin tick = offline

  const formatAge = (secs) => {
    if (secs < 60) return `${secs}s`;
    if (secs < 3600) return `${Math.floor(secs / 60)}m ${secs % 60}s`;
    const hours = Math.floor(secs / 3600);
    const mins = Math.floor((secs % 3600) / 60);
    return `${hours}h ${mins}m`;
  };

  // Reloj en vivo
  useEffect(() => {
    function tick() {
      const d = new Date();
      const h = d.getHours(), m = d.getMinutes(), s = d.getSeconds();
      const ampm = h >= 12 ? 'p. m.' : 'a. m.';
      setClock(`${String(h%12||12).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')} ${ampm}`);
    }
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, []);

  // Timer para recalcular edad del último tick cada 10s.
  useEffect(() => {
    const t = setInterval(() => setNowTick(Date.now()), 10000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="topbar">
      <div className="topbar-left">
        <span className="topbar-brand-logo">
          <EELogo size={18} />
        </span>
        <button className="icon-btn hamburger-btn" onClick={onMenuOpen} aria-label="Menú">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M3 6h18M3 12h18M3 18h18" />
          </svg>
        </button>
        <span className={`status-chip ${connected ? '' : 'offline'}`}>
          <span className="pulse" style={{ background: connected ? 'var(--pos)' : 'var(--muted)', animation: connected ? undefined : 'none' }} />
          {connected ? 'Conectado' : 'Sin conexión'}
        </span>

        {/* Chip de alerta compacto cuando motor offline */}
        {motorOffline && (
          <span
            className="motor-off-chip"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '4px 10px',
              borderRadius: 999,
              background: 'var(--neg-soft)',
              color: 'var(--neg)',
              fontSize: 11,
              fontWeight: 600,
              border: '0.5px solid color-mix(in oklab, var(--neg) 30%, transparent)',
              whiteSpace: 'nowrap',
            }}
          >
            <span style={{
              display: 'inline-block',
              width: 6,
              height: 6,
              borderRadius: '50%',
              background: 'var(--neg)',
              animation: 'pulse 2s infinite',
            }} />
            Motor off · {formatAge(ageSeconds)}
          </span>
        )}

        <span className="status-chip hide-mobile" style={{ color: 'var(--muted)' }}>
          <IconRefresh /><span>Auto · cada 7s</span>
        </span>
      </div>

      <div className="topbar-right">
        <span className="rb hide-mobile">
          <span className="dot" style={{ background: getAgeColor ? getAgeColor() : 'var(--accent)' }} />
          <span>Último tick <b style={{ color: 'var(--ink-2)' }}>{getAgeStr ? getAgeStr() : '—'}</b></span>
        </span>
        <span className="hide-mobile">
          Software <b style={{ color: 'var(--ink-2)', fontWeight: 500 }}>V1.6</b> · MatiEdgePro
        </span>
        <span className="clock hide-mobile">{clock}</span>

        {/* Toggle de tema dark/light — default dark. Persiste en localStorage
            y solo aplica dentro del dashboard (ver DashboardTheme.jsx). */}
        <button
          type="button"
          className="icon-btn theme-toggle-btn"
          onClick={toggleTheme}
          aria-label={theme === 'dark' ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}
          title={theme === 'dark' ? 'Modo claro' : 'Modo oscuro'}
        >
          {theme === 'dark' ? <IconSun /> : <IconMoon />}
        </button>

        {/* Avatar mobile — solo visible en mobile (CSS responsive).
            Reusa UserDropdown (bottom sheet en mobile) para no
            duplicar la UI de Mi cuenta / Configuración / Logout. */}
        <button
          ref={avatarRef}
          type="button"
          data-userdropdown-trigger="true"
          aria-haspopup="menu"
          aria-expanded={dropdownOpen}
          aria-label="Mi perfil"
          onClick={toggleDropdown}
          className="topbar-avatar-mobile"
        >
          <span className="topbar-avatar-circle">{initials}</span>
          <span className="topbar-avatar-label">{displayName.split(' ')[0]}</span>
        </button>
      </div>

      <UserDropdown
        open={dropdownOpen}
        triggerRect={triggerRect}
        onClose={() => setDropdownOpen(false)}
      />
    </div>
  );
}
